import { ChangeEvent, KeyboardEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { format } from "date-fns";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import {
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  Collapse,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  GlobalStyles,
  Badge,
  Menu,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha
} from "@mui/material";
import { Theme, ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
import useDebounce from "../../../hooks/useDebounce";
import useResponsive from "../../../minimals/hooks/useResponsive";
import { HeaderSlotContext } from "../../components/layouts/dashboard/HeaderSlotContext";
import Iconify from "../../../minimals/components/iconify";
import { logEvent, logOrderSearchFeedback } from "../../utils/analyticsLogger";
import { AdvancedOrderSearchRequest, DocumentsOrderInfo, OrderSearchDocument } from "../types";
import { OrderSummaryCard } from "./OrderSummaryCard";

const storageKey = "order-history";
const maxHistorySize = 10;
type SearchSort = "bestMatch" | "newest";

const emptyAdvancedSearch: AdvancedOrderSearchRequest = {
  address: "",
  city: "",
  state: "",
  county: "",
  section: "",
  block: "",
  lot: "",
  parcelId: "",
  transactingParty: "",
  exchangeFilesOnly: false
};

const states = [
  "",
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "DC",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY"
];

const floatingBarButtonSx = {
  flexShrink: 0,
  width: 40,
  height: 40,
  color: "primary.dark",
  backgroundColor: "background.paper",
  border: "1px solid",
  borderColor: "rgba(145, 158, 171, 0.48)",
  boxShadow: "0 6px 18px rgba(22, 28, 36, 0.18)",
  "&:hover": { backgroundColor: "background.paper" }
};

// The inline mobile filters: buttons rather than chips, which read as passive tags.
const inlineFilterSx = {
  flexShrink: 0,
  height: 32,
  px: 1.5,
  borderRadius: 1,
  fontSize: 13,
  fontWeight: 700,
  textTransform: "none" as const,
  whiteSpace: "nowrap" as const
};

const filterButtonSx = {
  borderRadius: 1,
  fontWeight: 700,
  textTransform: "none" as const
};

// Chip-style delete affordance: a filled grey circle with an x, sitting beside the
// control it clears.
const clearCircleSx = {
  width: 20,
  height: 20,
  p: 0,
  flexShrink: 0,
  color: "common.white",
  backgroundColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.26),
  "&:hover": {
    backgroundColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.4)
  }
};

const isMadison1031Email = (email?: string) => email?.toLowerCase().endsWith("@madison1031.com") === true;

const isCanceledOrDuplicate = (order: OrderSearchDocument) =>
  ["canceled", "duplicate"].includes(order.status?.toLowerCase() ?? "");

export function OrderSearchHome({
  initialQuery,
  onQueryChange,
  onOpenOrder,
  stickyTop
}: {
  initialQuery: string;
  onQueryChange: (query: string) => void;
  onOpenOrder: (orderNumber: string) => void;
  stickyTop: number;
}) {
  // Below lg the search bar moves up into the site header (next to the hamburger),
  // matching where the header shows the hamburger.
  const inHeader = useResponsive("down", "lg");
  const { slotEl } = useContext(HeaderSlotContext);

  // On mobile, make every MUI control render at its compact "small" size. Scoped to
  // this page only, and only applied below lg — desktop keeps the base theme verbatim.
  const baseTheme = useTheme();
  const compactTheme = useMemo(
    () =>
      createTheme(baseTheme, {
        components: {
          MuiTextField: { defaultProps: { size: "small" } },
          MuiCheckbox: { defaultProps: { size: "small" } },
          MuiButton: { defaultProps: { size: "small" } },
          MuiButtonGroup: { defaultProps: { size: "small" } },
          MuiIconButton: { defaultProps: { size: "small" } },
          MuiChip: { defaultProps: { size: "small" } },
          MuiFormControl: { defaultProps: { size: "small" } },
          MuiSelect: { defaultProps: { size: "small" } }
        }
      }),
    [baseTheme]
  );

  const [orders, setOrders] = useState<OrderSearchDocument[]>([]);
  const [searchText, setSearchText] = useState(initialQuery);
  const [isSearching, setIsSearching] = useState(false);
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [sortMenuAnchor, setSortMenuAnchor] = useState<HTMLElement | null>(null);
  // Mobile only: while the field has focus the action bar pins to the top of the
  // screen, out of the keyboard's way.
  const [searchFocused, setSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [keyboardInset, setKeyboardInset] = useState(0);

  // iOS never shrinks the layout viewport for the keyboard, so `position: fixed;
  // bottom: 0` sits behind it — worst in a standalone PWA, where there's no browser
  // chrome absorbing any of it. visualViewport reports what the keyboard actually
  // covers, so the bar can be lifted by exactly that much.
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!inHeader || !viewport) {
      setKeyboardInset(0);
      return;
    }
    const update = () => {
      const covered = window.innerHeight - (viewport.height + viewport.offsetTop);
      setKeyboardInset(Math.max(0, Math.round(covered)));
    };
    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
    };
  }, [inHeader]);
  const [hideMenuAnchor, setHideMenuAnchor] = useState<HTMLElement | null>(null);
  const [advancedSearchMode, setAdvancedSearchMode] = useState(false);
  const [showResultLimitNotice, setShowResultLimitNotice] = useState(false);
  const exchangeFilesOnlyDefault = window.localStorage.getItem("exchange-only")?.toLowerCase() === "true";
  const [exchangeFilesOnly, setExchangeFilesOnly] = useState(exchangeFilesOnlyDefault);
  const [hideCanceledAndDuplicate, setHideCanceledAndDuplicate] = useState(true);
  const [hideExchange, setHideExchange] = useState(!exchangeFilesOnlyDefault);
  const [searchSort, setSearchSort] = useState<SearchSort>("bestMatch");
  const [userDefaultsLoaded, setUserDefaultsLoaded] = useState(false);
  const [searchFeedback, setSearchFeedback] = useState({
    submitted: false,
    showNotes: false,
    notes: ""
  });
  const [advancedSearch, setAdvancedSearch] = useState<AdvancedOrderSearchRequest>(emptyAdvancedSearch);
  // Quick view: the order page's summary card in a sheet, without leaving the results.
  const [quickViewOrderNumber, setQuickViewOrderNumber] = useState<string | null>(null);
  const [quickViewInfo, setQuickViewInfo] = useState<DocumentsOrderInfo | null>(null);
  const [quickViewLoading, setQuickViewLoading] = useState(false);
  const debouncedSearchTerm = useDebounce(searchText || "", 500);
  const orderHistory = useMemo(() => getOrderHistory(), [orders.length, advancedSearchMode, searchText]);
  const searchRequestId = useRef(0);

  useEffect(() => {
    if (initialQuery !== searchText) {
      setSearchText(initialQuery);
    }
  }, [initialQuery, searchText]);

  useEffect(() => {
    const applyUserSearchDefaults = async () => {
      try {
        const { data } = await axios.get<{ email?: string }>("/api/Login/GetUserPersonalInfo");
        if (isMadison1031Email(data?.email)) {
          setExchangeFilesOnly(true);
          setHideExchange(false);
        }
      } finally {
        setUserDefaultsLoaded(true);
      }
    };

    applyUserSearchDefaults();
  }, []);

  useEffect(() => {
    if (!userDefaultsLoaded) {
      return;
    }

    const doSearch = async () => {
      const requestId = searchRequestId.current + 1;
      searchRequestId.current = requestId;
      setIsSearching(true);

      try {
        if (debouncedSearchTerm) {
          const searchParams = {
            query: debouncedSearchTerm,
            exchangeOnly: exchangeFilesOnly,
            sortBy: searchSort,
            hideCanceledAndDuplicate,
            hideExchange
          };
          const { data: initialOrders } = await axios.get<OrderSearchDocument[]>(
            "/api/orderdocuments/searching/search",
            { params: searchParams }
          );

          let data = initialOrders ?? [];
          let effectiveHideCanceledAndDuplicate = hideCanceledAndDuplicate;
          if (!data.length && hideCanceledAndDuplicate) {
            const { data: unfilteredOrders } = await axios.get<OrderSearchDocument[]>(
              "/api/orderdocuments/searching/search",
              {
                params: {
                  ...searchParams,
                  hideCanceledAndDuplicate: false
                }
              }
            );
            if (unfilteredOrders?.length === 1 && isCanceledOrDuplicate(unfilteredOrders[0])) {
              data = unfilteredOrders;
              effectiveHideCanceledAndDuplicate = false;
            }
          }

          if (requestId === searchRequestId.current) {
            if (!effectiveHideCanceledAndDuplicate) {
              setHideCanceledAndDuplicate(false);
            }
            setOrders(data);
            setShowResultLimitNotice(data.length >= 200);
            setSearchFeedback({ submitted: false, showNotes: false, notes: "" });
            logEvent(
              "Search Page",
              "Search by typing",
              JSON.stringify({
                searchText: debouncedSearchTerm,
                resultCount: data.length,
                sortBy: searchSort,
                hideCanceledAndDuplicate: effectiveHideCanceledAndDuplicate,
                hideExchange
              })
            );
          }
        } else {
          if (requestId === searchRequestId.current) {
            setOrders([]);
            setShowResultLimitNotice(false);
          }
        }
        if (requestId === searchRequestId.current) {
          setAdvancedSearchMode(false);
        }
      } finally {
        if (requestId === searchRequestId.current) {
          setIsSearching(false);
        }
      }
    };

    doSearch();
  }, [debouncedSearchTerm, exchangeFilesOnly, hideCanceledAndDuplicate, hideExchange, searchSort, userDefaultsLoaded]);

  const openOrder = (order: OrderSearchDocument, logClick = true) => {
    addOrderToHistory(order);
    if (logClick) {
      logEvent(
        "Search Page",
        searchText || advancedSearchMode ? "Search results table click" : "History table click",
        order.orderNumber
      );
    }
    onOpenOrder(order.orderNumber);
  };

  const handleSearchKeyDown = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter" || !searchText?.trim()) {
      return;
    }

    const { data } = await axios.get<OrderSearchDocument[]>("/api/orderdocuments/searching/search", {
      params: {
        query: searchText.trim(),
        exchangeOnly: exchangeFilesOnly,
        sortBy: searchSort,
        hideCanceledAndDuplicate,
        hideExchange
      }
    });

    logEvent(
      "Search Page",
      "Search by pressing enter",
      JSON.stringify({
        searchText: searchText.trim(),
        resultCount: data.length,
        sortBy: searchSort,
        hideCanceledAndDuplicate,
        hideExchange
      })
    );

    if (!data.length) {
      onOpenOrder(searchText.trim());
      return;
    }

    const exactOrder = data.find((order) => order.orderNumber.toLowerCase() === searchText.trim().toLowerCase());
    if (exactOrder) {
      openOrder(exactOrder, false);
    }
  };

  // "Exchange files only" is a single page-level toggle (in the filters drawer) rather
  // than a separate copy inside the panel, so it is merged in here for both searches.
  const runAdvancedSearch = async (
    criteria: AdvancedOrderSearchRequest = advancedSearch,
    exchangeOnly: boolean = exchangeFilesOnly
  ) => {
    searchRequestId.current += 1;
    setIsSearching(true);
    try {
      const { data } = await axios.get<OrderSearchDocument[]>("/api/orderdocuments/searching/advancedsearch", {
        params: { ...criteria, exchangeFilesOnly: exchangeOnly }
      });
      setOrders(data ?? []);
      setShowResultLimitNotice((data?.length ?? 0) >= 200);
      setAdvancedSearchMode(true);
      setAdvancedSearchOpen(false);
      setSearchFeedback({ submitted: false, showNotes: false, notes: "" });
      logEvent(
        "Search Page",
        "Advanced search",
        JSON.stringify({ advancedSearch: criteria, resultCount: data?.length ?? 0 })
      );
    } finally {
      setIsSearching(false);
    }
  };

  // Criteria behind the current advanced-search results, shown as removable chips so
  // it is obvious what produced the list.
  const advancedFieldLabels: Partial<Record<keyof AdvancedOrderSearchRequest, string>> = {
    address: "Address",
    city: "City",
    state: "State",
    county: "County",
    section: "Section",
    block: "Block",
    lot: "Lot",
    parcelId: "APN/Parcel ID",
    transactingParty: "Buyer/Seller/Attorney/Principal"
  };

  const activeAdvancedCriteria = advancedSearchMode
    ? (Object.keys(advancedFieldLabels) as (keyof AdvancedOrderSearchRequest)[])
        .filter((field) => {
          const fieldValue = advancedSearch[field];
          return typeof fieldValue === "boolean" ? fieldValue : Boolean(fieldValue);
        })
        .map((field) => ({
          field,
          label: advancedFieldLabels[field],
          value: advancedSearch[field]
        }))
    : [];

  const removeAdvancedCriterion = (field: keyof AdvancedOrderSearchRequest) => {
    const next: AdvancedOrderSearchRequest = {
      ...advancedSearch,
      [field]: typeof advancedSearch[field] === "boolean" ? false : ""
    };
    setAdvancedSearch(next);

    const stillFiltered = (Object.keys(advancedFieldLabels) as (keyof AdvancedOrderSearchRequest)[]).some((key) =>
      typeof next[key] === "boolean" ? next[key] : Boolean(next[key])
    );

    if (stillFiltered) {
      runAdvancedSearch(next);
    } else {
      // Nothing left to filter on — drop back to the plain search view.
      setAdvancedSearchMode(false);
      setOrders([]);
      setShowResultLimitNotice(false);
    }
  };

  const clearAdvancedSearch = () => {
    setAdvancedSearch(emptyAdvancedSearch);
    setAdvancedSearchMode(false);
    setOrders([]);
    setShowResultLimitNotice(false);
  };

  // Back to the page defaults the active-filter badge is measured against.
  const resetFilters = () => {
    window.localStorage.setItem("exchange-only", "false");
    setExchangeFilesOnly(false);
    setSearchSort("bestMatch");
    setHideCanceledAndDuplicate(true);
    setHideExchange(true);
    logEvent("Search Page", "Search filters cleared", "");
  };

  const loadQuickView = async (orderNumber: string) => {
    setQuickViewOrderNumber(orderNumber);
    setQuickViewInfo(null);
    setQuickViewLoading(true);
    try {
      const { data } = await axios.get<DocumentsOrderInfo>(
        `/api/orderdocuments/orderinfo?orderNumber=${encodeURIComponent(orderNumber)}`
      );
      setQuickViewInfo(data);
    } catch {
      setQuickViewInfo(null);
    } finally {
      setQuickViewLoading(false);
    }
  };

  const quickViewRelatedOrders =
    quickViewInfo?.relatedOrders
      ?.split(/,(?![^()]*\))/)
      .map((relatedOrder) => relatedOrder.trim())
      .filter(Boolean) ?? [];

  const visibleOrders = searchText || advancedSearchMode ? orders : orderHistory;
  const tableHeader = searchText || advancedSearchMode ? "Search Results" : "Recent Orders";
  const showNoResults =
    !isSearching && ((debouncedSearchTerm && searchText) || advancedSearchMode) && orders.length === 0;

  const exportSearchResults = () => {
    const headers = [
      "Order Number",
      "Project Name",
      "Closing Date",
      "Address",
      "City",
      "State",
      "Additional Property Addresses",
      "Block/Lot/Parcel",
      "Marketing Source Type",
      "Marketing Source",
      "Buyer",
      "Seller",
      "Principal",
      "Status"
    ];
    const rows = orders.map((order) => [
      order.orderNumber,
      order.projectName,
      formatExportDate(order.settlementDate),
      order.address,
      order.city,
      order.state,
      order.additionalPropertyAddresses?.join("; "),
      getBlockLotParcel(order),
      order.marketingSourceType,
      order.marketingSource,
      order.buyer,
      order.seller,
      order.principal,
      order.status
    ]);
    const csv = [headers, ...rows].map((row) => row.map((value) => escapeCsvValue(value)).join(",")).join("\r\n");
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");

    link.href = URL.createObjectURL(blob);
    link.download = `titleq-search-results-${timestamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);

    logEvent(
      "Search Page",
      "Export search results",
      JSON.stringify({ resultCount: orders.length, searchText: searchText.trim() })
    );
  };

  const submitSearchFeedback = async (isHelpful: boolean, notes?: string) => {
    await logOrderSearchFeedback({
      isHelpful,
      notes,
      searchText: advancedSearchMode ? JSON.stringify(advancedSearch) : debouncedSearchTerm,
      selectedOrderNumber: null,
      resultCount: orders.length,
      resultsJson: JSON.stringify(
        orders.map((order, index) => ({
          rank: index + 1,
          orderNumber: order.orderNumber,
          address: order.address,
          city: order.city,
          state: order.state,
          status: order.status
        }))
      ),
      sortBy: advancedSearchMode ? "advanced" : searchSort
    });
    setSearchFeedback({ submitted: true, showNotes: false, notes: "" });
  };

  const searchField = (
    <TextField
      inputRef={searchInputRef}
      onFocus={() => {
        if (inHeader) {
          setSearchFocused(true);
        }
      }}
      // Dismissing the keyboard any other way returns the bar to the bottom.
      onBlur={() => setSearchFocused(false)}
      // Focused on load everywhere: this page exists to be typed into.
      autoFocus
      fullWidth
      variant="filled"
      hiddenLabel
      placeholder={inHeader ? "Search orders" : "Search for file number, contact name or address"}
      value={searchText ?? ""}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        const nextSearchText = event.target.value;
        setSearchText(nextSearchText);
        onQueryChange(nextSearchText);
        setShowResultLimitNotice(false);
      }}
      onKeyDown={handleSearchKeyDown}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Iconify icon="eva:search-fill" />
          </InputAdornment>
        )
      }}
      sx={{
        flex: 1,
        // Fill, radius and underline removal all come from the theme's MuiFilledInput
        // override (the minimals "contained" input) — only the type scale is ours.
        "& .MuiFilledInput-root": {
          // 16 is a floor, not a preference: iOS Safari auto-zooms the page when a
          // focused input is smaller than that, and never zooms back out.
          fontSize: { xs: 16, md: 20 },
          fontWeight: 700
        },
        "& .MuiInputBase-input": {
          py: 1.2
        }
      }}
    />
  );

  const exchangeOnlyControl = (
    <FormControlLabel
      control={
        <Checkbox
          checked={exchangeFilesOnly}
          onChange={(_, checked) => {
            window.localStorage.setItem("exchange-only", `${checked}`);
            setExchangeFilesOnly(checked);
            logEvent("Search Page", "Search filter changed", JSON.stringify({ exchangeFilesOnly: checked }));
            if (checked) {
              setHideExchange(false);
            }
            // The plain search re-runs from its effect; advanced results have to be
            // asked for again explicitly, with the value we just set.
            if (advancedSearchMode) {
              runAdvancedSearch(advancedSearch, checked);
            }
          }}
          size="small"
        />
      }
      label="Exchange files only"
      sx={{ my: 0 }}
    />
  );

  const sortByControl = (
    <ButtonGroup size="small" variant="outlined" aria-label="Search result sorting">
      <Button
        disabled={isSearching}
        onClick={() => {
          setSearchSort("bestMatch");
          logEvent("Search Page", "Search sort changed", "bestMatch");
        }}
        variant={searchSort === "bestMatch" ? "contained" : "outlined"}
      >
        Best match
      </Button>
      <Button
        disabled={isSearching}
        onClick={() => {
          setSearchSort("newest");
          logEvent("Search Page", "Search sort changed", "newest");
        }}
        variant={searchSort === "newest" ? "contained" : "outlined"}
      >
        Newest
      </Button>
    </ButtonGroup>
  );

  const hideControls = (
    <>
      <FormControlLabel
        control={
          <Checkbox
            checked={hideCanceledAndDuplicate}
            disabled={isSearching}
            onChange={(_, checked) => {
              setHideCanceledAndDuplicate(checked);
              logEvent("Search Page", "Search filter changed", JSON.stringify({ hideCanceledAndDuplicate: checked }));
            }}
            size="small"
          />
        }
        label="Canceled/Duplicate"
      />
      <FormControlLabel
        control={
          <Checkbox
            checked={hideExchange}
            disabled={isSearching || exchangeFilesOnly}
            onChange={(_, checked) => {
              setHideExchange(checked);
              logEvent("Search Page", "Search filter changed", JSON.stringify({ hideExchange: checked }));
            }}
            size="small"
          />
        }
        label="Exchange"
      />
    </>
  );

  const hideCount = (hideCanceledAndDuplicate ? 1 : 0) + (hideExchange ? 1 : 0);

  // Few enough filters to sit inline above the results rather than behind a drawer.
  // The row scrolls sideways rather than wrapping if a label runs long.
  const mobileFilterRow = (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      sx={{
        overflowX: "auto",
        pb: 2,
        "&::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none"
      }}
    >
      <Button
        variant={exchangeFilesOnly ? "contained" : "outlined"}
        sx={inlineFilterSx}
        onClick={() => {
          const checked = !exchangeFilesOnly;
          window.localStorage.setItem("exchange-only", `${checked}`);
          setExchangeFilesOnly(checked);
          logEvent("Search Page", "Search filter changed", JSON.stringify({ exchangeFilesOnly: checked }));
          if (checked) {
            setHideExchange(false);
          }
          if (advancedSearchMode) {
            runAdvancedSearch(advancedSearch, checked);
          }
        }}
      >
        Exchange only
      </Button>

      <Button
        variant="outlined"
        disabled={isSearching}
        onClick={(event) => setSortMenuAnchor(event.currentTarget)}
        endIcon={<Iconify icon="eva:chevron-down-fill" width={16} />}
        sx={inlineFilterSx}
      >
        {searchSort === "bestMatch" ? "Best match" : "Newest"}
      </Button>

      <Button
        variant={hideCount > 0 ? "contained" : "outlined"}
        onClick={(event) => setHideMenuAnchor(event.currentTarget)}
        endIcon={<Iconify icon="eva:chevron-down-fill" width={16} />}
        sx={inlineFilterSx}
      >
        {hideCount > 0 ? `Hiding ${hideCount}` : "Hide"}
      </Button>
    </Stack>
  );

  const mobileFilterMenus = (
    <>
      <Menu anchorEl={sortMenuAnchor} open={Boolean(sortMenuAnchor)} onClose={() => setSortMenuAnchor(null)}>
        {[
          { value: "bestMatch" as const, label: "Best match" },
          { value: "newest" as const, label: "Newest" }
        ].map((option) => (
          <MenuItem
            key={option.value}
            selected={searchSort === option.value}
            onClick={() => {
              setSearchSort(option.value);
              logEvent("Search Page", "Search sort changed", option.value);
              setSortMenuAnchor(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={hideMenuAnchor} open={Boolean(hideMenuAnchor)} onClose={() => setHideMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            const checked = !hideCanceledAndDuplicate;
            setHideCanceledAndDuplicate(checked);
            logEvent("Search Page", "Search filter changed", JSON.stringify({ hideCanceledAndDuplicate: checked }));
          }}
        >
          <Checkbox checked={hideCanceledAndDuplicate} size="small" sx={{ p: 0, mr: 1 }} />
          Canceled/Duplicate
        </MenuItem>
        <MenuItem
          disabled={exchangeFilesOnly}
          onClick={() => {
            const checked = !hideExchange;
            setHideExchange(checked);
            logEvent("Search Page", "Search filter changed", JSON.stringify({ hideExchange: checked }));
          }}
        >
          <Checkbox checked={hideExchange} size="small" sx={{ p: 0, mr: 1 }} />
          Exchange
        </MenuItem>
      </Menu>
    </>
  );

  // Mobile advanced search: a rounded card that slides up from the bottom of the screen.
  const advancedSearchSheet = (
    <Drawer
      anchor="bottom"
      open={advancedSearchOpen}
      onClose={() => setAdvancedSearchOpen(false)}
      PaperProps={{
        sx: {
          borderRadius: "20px 20px 0 0",
          maxHeight: "88vh",
          px: 2,
          pb: 2
        }
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 2, pb: 1 }}>
        <Typography sx={{ fontWeight: 700 }}>Advanced Search</Typography>
        <IconButton size="small" onClick={() => setAdvancedSearchOpen(false)}>
          <Iconify icon="eva:close-fill" />
        </IconButton>
      </Stack>
      <Divider sx={{ mx: -2 }} />

      <Box sx={{ overflowY: "auto", pt: 2 }}>
        <AdvancedSearchPanel
          bare
          value={advancedSearch}
          onChange={setAdvancedSearch}
          onClear={() => setAdvancedSearch(emptyAdvancedSearch)}
          onSearch={runAdvancedSearch}
        />
      </Box>
    </Drawer>
  );

  // Same summary card the order page shows, in a sheet, so results can be inspected
  // without navigating away.
  const quickViewSheet = (
    <Drawer
      anchor="bottom"
      open={Boolean(quickViewOrderNumber)}
      onClose={() => setQuickViewOrderNumber(null)}
      // Detached sheet: inset from every edge with all four corners rounded, so it
      // floats over the results rather than being anchored to the screen bottom.
      PaperProps={{
        sx: {
          m: 2,
          width: "auto",
          borderRadius: 3,
          maxHeight: "82vh",
          px: 2,
          pb: 2
        }
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pt: 2, pb: 1 }}>
        {/* The order number is the title — matches the card's own heading, which
            prefixes the title code when the order has one. */}
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 700,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap"
          }}
        >
          {quickViewInfo?.titleCode
            ? `${quickViewInfo.titleCode}-${quickViewInfo.number || quickViewOrderNumber}`
            : quickViewOrderNumber}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              if (quickViewOrderNumber) {
                const order = visibleOrders.find((item) => item.orderNumber === quickViewOrderNumber);
                setQuickViewOrderNumber(null);
                if (order) {
                  openOrder(order);
                } else {
                  onOpenOrder(quickViewOrderNumber);
                }
              }
            }}
            sx={{ borderRadius: 1, fontWeight: 700, textTransform: "none" }}
          >
            Open
          </Button>
          <IconButton size="small" onClick={() => setQuickViewOrderNumber(null)}>
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Stack>
      </Stack>
      <Divider sx={{ mx: -2 }} />

      <Box
        sx={{
          overflowY: "auto",
          pt: 0.5,
          // Cancel the sheet's px:2 so the card isn't double-padded; the card's own
          // px provides the inset, then keep 10px of extra breathing room at the sides.
          mx: -0.75,
          // The sheet is the card here, so drop the summary card's own chrome.
          "& > .MuiPaper-root": { border: 0, boxShadow: "none" }
        }}
      >
        {quickViewOrderNumber && (
          <OrderSummaryCard
            orderNumber={quickViewOrderNumber}
            orderInfo={quickViewInfo}
            relatedOrders={quickViewRelatedOrders}
            isLoading={quickViewLoading}
            compactLoading
            onOrderInfoUpdated={async () => {
              await loadQuickView(quickViewOrderNumber);
            }}
          />
        )}
      </Box>
    </Drawer>
  );

  const page = (
    <>
      {/* Main's desktop padding is HEADER.H_DASHBOARD_DESKTOP + SPACING = 100px on
          both sides, so a full-height page still left 100px of dead space under it.
          The top padding is the fixed header's offset and has to stay; the bottom
          one only needs enough to breathe. */}
      <GlobalStyles
        styles={{
          "@media (min-width:1200px)": {
            main: { paddingBottom: "24px !important" }
          }
        }}
      />
      <Box
        sx={{
          // Definite height, not just a floor: the results card sizes itself against
          // this, filling what's left rather than stopping at a guessed offset.
          // 124 = 100 top padding + the 24 bottom set above.
          // lg, not md: `inHeader` switches to the mobile layout below lg, so between
          // 900 and 1200px the floating bar renders. Keying these to md left that range
          // with no clearance and a fixed height, so content ran under the bar.
          height: { xs: "auto", lg: "calc(100vh - 124px)" },
          minHeight: { xs: "calc(100vh - 180px)", lg: "calc(100vh - 124px)" },
          pb: { xs: 12, lg: 0 },
          display: "flex",
          justifyContent: "center"
        }}
      >
        <Stack spacing={{ xs: 0, lg: 2 }} sx={{ width: "min(100%, 1540px)", minHeight: 0 }}>
          {/* Always occupies its 3px: mounting and unmounting the track on every
              keystroke shifted everything below it while typing. */}
          <Box
            sx={{
              height: 3,
              flexShrink: 0,
              overflow: "hidden",
              borderRadius: 999,
              visibility: isSearching ? "visible" : "hidden",
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12)
            }}
          >
            {isSearching && (
              <Box
                sx={{
                  width: "35%",
                  height: "100%",
                  backgroundColor: "primary.main",
                  animation: "order-search-loading 1.1s ease-in-out infinite",
                  "@keyframes order-search-loading": {
                    "0%": { transform: "translateX(-100%)" },
                    "100%": { transform: "translateX(330%)" }
                  }
                }}
              />
            )}
          </Box>

          {inHeader && slotEl
            ? createPortal(
                <Typography
                  noWrap
                  sx={{ color: "primary.dark", fontSize: 17, fontWeight: 700, lineHeight: 1.2, minWidth: 0 }}
                >
                  Documents
                </Typography>,
                slotEl
              )
            : null}

          <Stack spacing={1.25}>
            {/* Mobile moves the field into the floating bar at the bottom of the screen,
                so this row renders nothing there — skip it rather than leave an empty
                flex child eating the Stack's spacing. */}
            {!inHeader && (
              <Stack direction={{ xs: "column", lg: "row" }} spacing={1.25} alignItems={{ lg: "center" }}>
                {searchField}
                {
                  <Paper
                    variant="outlined"
                    sx={{
                      flexShrink: 0,
                      p: 0.75,
                      borderRadius: 1,
                      borderColor: (theme) => alpha(theme.palette.grey[500], 0.16),
                      backgroundColor: "background.paper"
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      {exchangeOnlyControl}
                      <Button
                        variant="outlined"
                        onClick={() => setAdvancedSearchOpen((current) => !current)}
                        sx={filterButtonSx}
                      >
                        Advanced Search
                      </Button>
                      {advancedSearchMode && (
                        <IconButton aria-label="Clear advanced search" onClick={clearAdvancedSearch} sx={clearCircleSx}>
                          <Iconify icon="eva:close-fill" width={14} />
                        </IconButton>
                      )}
                    </Stack>
                  </Paper>
                }
              </Stack>
            )}

            {/* Desktop expands the panel inline; mobile gets a bottom sheet (below). */}
            {!inHeader && (
              <Collapse in={advancedSearchOpen} timeout="auto">
                <AdvancedSearchPanel
                  value={advancedSearch}
                  onChange={setAdvancedSearch}
                  onClear={() => setAdvancedSearch(emptyAdvancedSearch)}
                  onSearch={runAdvancedSearch}
                />
              </Collapse>
            )}

            {activeAdvancedCriteria.length > 0 && !advancedSearchOpen && (
              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 600 }}>
                  Advanced:
                </Typography>
                {activeAdvancedCriteria.map((criterion) => (
                  <Chip
                    key={criterion.field}
                    size="small"
                    variant="outlined"
                    onDelete={() => removeAdvancedCriterion(criterion.field)}
                    label={
                      typeof criterion.value === "boolean" ? criterion.label : `${criterion.label}: ${criterion.value}`
                    }
                    sx={{ borderRadius: 1, fontWeight: 600 }}
                  />
                ))}
              </Stack>
            )}
          </Stack>

          {/* Both children of this bar are desktop-only now — sort/hide moved into the
            mobile filters drawer and the feedback prompt is hidden below lg — so on
            mobile the bar would render as an empty outlined strip. */}
          {!inHeader &&
            ((!!searchText && !advancedSearchMode) || (orders.length > 0 && (searchText || advancedSearchMode))) && (
              <Paper
                variant="outlined"
                sx={{
                  p: 0.5,
                  borderRadius: 1,
                  borderColor: (theme) => alpha(theme.palette.grey[500], 0.2),
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.025),
                  boxShadow: "0 2px 8px rgba(22, 28, 36, 0.04)"
                }}
              >
                <Stack
                  direction={{ xs: "column", lg: "row" }}
                  spacing={0.75}
                  alignItems={{ xs: "stretch", lg: "center" }}
                  justifyContent="space-between"
                >
                  {!!searchText && !advancedSearchMode && !inHeader && (
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          alignItems: "center",
                          backgroundColor: "background.paper",
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          display: "flex",
                          gap: 1,
                          p: 0.25
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary", pl: 0.5 }}>
                          Sort by
                        </Typography>
                        {sortByControl}
                      </Box>
                      <Box
                        sx={{
                          alignItems: "center",
                          backgroundColor: "background.paper",
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1,
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          px: 1
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: "text.secondary" }}>
                          Hide
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}>
                          {hideControls}
                        </Box>
                      </Box>
                    </Stack>
                  )}

                  {orders.length > 0 && (searchText || advancedSearchMode) && (
                    <Box
                      sx={{
                        // Feedback prompt is desktop-only; it's noise on a phone.
                        display: { xs: "none", lg: "block" },
                        borderColor: "divider",
                        borderLeft: { xs: "none", lg: "1px solid" },
                        borderTop: { xs: "1px solid", lg: "none" },
                        minWidth: { lg: 330 },
                        pt: { xs: 0.75, lg: 0 },
                        pl: { xs: 0, lg: 1.5 }
                      }}
                    >
                      {searchFeedback.submitted ? (
                        <Typography color="text.secondary" variant="body2" sx={{ textAlign: "center" }}>
                          Thanks for the feedback.
                        </Typography>
                      ) : (
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                          <Box sx={{ textAlign: "center" }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              Are these results relevant?
                            </Typography>
                            <Typography color="text.secondary" variant="caption">
                              Help us tune search.
                            </Typography>
                          </Box>
                          <IconButton
                            aria-label="Mark search results helpful"
                            color="success"
                            onClick={() => submitSearchFeedback(true)}
                            size="small"
                          >
                            <ThumbUpIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            aria-label="Mark search results not helpful"
                            color="error"
                            onClick={() => setSearchFeedback({ submitted: false, showNotes: true, notes: "" })}
                            size="small"
                          >
                            <ThumbDownIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      )}
                    </Box>
                  )}
                </Stack>
              </Paper>
            )}

          {inHeader && mobileFilterRow}

          {visibleOrders.length > 0 && (
            <OrdersTable
              orders={visibleOrders}
              searchText={debouncedSearchTerm}
              headerText={tableHeader}
              onOrderClick={openOrder}
              onQuickView={loadQuickView}
              showResultLimitNotice={showResultLimitNotice}
              onExport={searchText || advancedSearchMode ? exportSearchResults : undefined}
              stickyTop={stickyTop}
            />
          )}

          {showNoResults && (
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                borderRadius: 1,
                textAlign: "center",
                borderColor: (theme) => alpha(theme.palette.grey[500], 0.2),
                backgroundColor: "background.paper"
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                No results found
              </Typography>
              <Typography color="text.secondary">
                Sorry about that. If you believe this is an error, please email{" "}
                <Link href="mailto:devsupport@madisoncres.com">devsupport@madisoncres.com</Link>.
              </Typography>
              {(hideCanceledAndDuplicate || hideExchange) && !advancedSearchMode && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    setHideCanceledAndDuplicate(false);
                    setHideExchange(false);
                  }}
                  sx={{ mt: 2, borderRadius: 1, fontWeight: 700, textTransform: "none" }}
                >
                  Clear result filters
                </Button>
              )}
            </Paper>
          )}

          <Dialog
            fullWidth
            maxWidth="sm"
            open={searchFeedback.showNotes}
            onClose={() => setSearchFeedback({ submitted: false, showNotes: false, notes: "" })}
          >
            <DialogTitle>What was wrong with these results?</DialogTitle>
            <DialogContent>
              <TextField
                autoFocus
                fullWidth
                multiline
                rows={4}
                placeholder="Tell us what you expected to find, or what felt off."
                value={searchFeedback.notes}
                onChange={(event) =>
                  setSearchFeedback({ submitted: false, showNotes: true, notes: event.target.value })
                }
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSearchFeedback({ submitted: false, showNotes: false, notes: "" })}>
                Cancel
              </Button>
              <Button
                disabled={!searchFeedback.notes.trim()}
                onClick={() => submitSearchFeedback(false, searchFeedback.notes)}
                variant="contained"
              >
                Submit feedback
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </Box>
    </>
  );

  // Desktop renders the page verbatim; mobile wraps it so all controls go compact.
  // Same floating bar as /calendar and the phonebook: search within thumb reach,
  // with filters and advanced search beside it.
  // Stays at the bottom and rides above the keyboard via the measured inset (see the
  // visualViewport effect). Positioning is a transform on one element, never a move
  // to a different parent — that would unmount the input and drop the keyboard.
  const mobileActionBar = (
    <Box
      sx={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: (theme: any) => theme.zIndex.drawer - 1,
        px: 1.5,
        pt: keyboardInset > 0 ? 1.5 : 4,
        // Lifted clear of the keyboard by its measured height. The safe-area inset
        // only applies when the keyboard is down; with it up, the keyboard covers
        // the home indicator anyway.
        pb: keyboardInset > 0 ? 1 : "calc(12px + env(safe-area-inset-bottom))",
        transform: `translateY(-${keyboardInset}px)`,
        backgroundColor: keyboardInset > 0 ? "background.paper" : undefined,
        boxShadow: keyboardInset > 0 ? "0 -6px 18px rgba(22, 28, 36, 0.14)" : undefined,
        // Gradient scrim rather than a panel: it fades out with no edge, so rows
        // don't show through the gaps without reading as a second layer.
        background:
          keyboardInset > 0
            ? undefined
            : (theme: any) =>
                `linear-gradient(to top, ${theme.palette.background.default} 55%, ${alpha(
                  theme.palette.background.default,
                  0
                )})`,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none"
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="stretch"
        sx={{ pointerEvents: "auto", width: 1, maxWidth: 560, minWidth: 0 }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            // Both the field and the button are pinned to the same height rather than
            // relying on stretch: the filled input's padding and the Button's own
            // min-height resolve differently.
            "& .MuiFilledInput-root, & .MuiOutlinedInput-root": {
              height: 44,
              borderRadius: 999,
              backgroundColor: "background.paper",
              // `.Mui-focused`/`:hover` are two-class selectors in the theme's filled
              // input, so they outrank the plain backgroundColor above and would put
              // the desktop grey fill back on focus.
              "&:hover, &.Mui-focused, &.Mui-focused:hover": {
                backgroundColor: "background.paper"
              },
              boxShadow: "0 6px 18px rgba(22, 28, 36, 0.18)",
              // Matches the border on the filter and Advanced buttons either side.
              border: "1px solid rgba(145, 158, 171, 0.48)",
              // The theme's filled input draws its own underline; the border replaces it.
              "&:before, &:after": { display: "none" }
            },
            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            "& .MuiInputBase-input": { py: 0 }
          }}
        >
          {searchField}
        </Box>

        <Button
          variant="contained"
          // Without this the button steals focus, onBlur clears searchFocused, and the
          // click below then reads it as false and opens Advanced Search instead.
          onPointerDown={(event) => {
            if (searchFocused) {
              event.preventDefault();
            }
          }}
          onClick={() => {
            if (searchFocused) {
              setSearchFocused(false);
              searchInputRef.current?.blur();
              return;
            }
            setAdvancedSearchOpen((current) => !current);
          }}
          sx={{
            flexShrink: 0,
            height: 44,
            px: 1.5,
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 700,
            whiteSpace: "nowrap",
            color: advancedSearchMode ? undefined : "primary.dark",
            backgroundColor: advancedSearchMode ? undefined : "background.paper",
            border: "1px solid",
            borderColor: "rgba(145, 158, 171, 0.48)",
            boxShadow: "0 6px 18px rgba(22, 28, 36, 0.18)",
            "&:hover": { backgroundColor: advancedSearchMode ? undefined : "background.paper" }
          }}
        >
          {searchFocused ? "Cancel" : "Advanced Search"}
        </Button>
      </Stack>
    </Box>
  );

  return inHeader ? (
    <ThemeProvider theme={compactTheme}>
      {page}
      {mobileActionBar}
      {mobileFilterMenus}
      {advancedSearchSheet}
      {quickViewSheet}
    </ThemeProvider>
  ) : (
    page
  );
}

function AdvancedSearchPanel({
  value,
  onChange,
  onClear,
  onSearch,
  bare = false
}: {
  value: AdvancedOrderSearchRequest;
  onChange: (value: AdvancedOrderSearchRequest) => void;
  onClear: () => void;
  onSearch: () => void;
  // Inside the mobile bottom sheet the sheet itself is the card, so the panel drops
  // its own border, fill and outer spacing.
  bare?: boolean;
}) {
  const setField = (field: keyof AdvancedOrderSearchRequest, fieldValue: string | boolean) => {
    onChange({ ...value, [field]: fieldValue });
  };

  return (
    <Box
      sx={{
        ...(bare
          ? {}
          : {
              mt: 1.5,
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              backgroundColor: "background.neutral"
            }),
        // Inputs are transparent by default, which leaves them washed out against the
        // panel's grey fill — give them the paper background so the fields read clearly.
        "& .MuiOutlinedInput-root": {
          backgroundColor: "background.paper"
        }
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
          gap: 1.5
        }}
      >
        <TextField
          size="small"
          label="Address"
          value={value.address}
          onChange={(event) => setField("address", event.target.value)}
        />
        <TextField
          size="small"
          label="City"
          value={value.city}
          onChange={(event) => setField("city", event.target.value)}
        />
        <TextField
          select
          size="small"
          label="State"
          value={value.state}
          onChange={(event) => setField("state", event.target.value)}
        >
          {states.map((state) => (
            <MenuItem key={state || "blank"} value={state}>
              {state}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="County"
          value={value.county}
          onChange={(event) => setField("county", event.target.value)}
        />
        <TextField
          size="small"
          label="Section"
          value={value.section}
          onChange={(event) => setField("section", event.target.value)}
        />
        <TextField
          size="small"
          label="Block"
          value={value.block}
          onChange={(event) => setField("block", event.target.value)}
        />
        <TextField
          size="small"
          label="Lot"
          value={value.lot}
          onChange={(event) => setField("lot", event.target.value)}
        />
        <TextField
          size="small"
          label="APN/Parcel ID"
          value={value.parcelId}
          onChange={(event) => setField("parcelId", event.target.value)}
        />
        <TextField
          size="small"
          label="Buyer/Seller/Attorney/Principal"
          value={value.transactingParty}
          onChange={(event) => setField("transactingParty", event.target.value)}
        />
      </Box>
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button variant="outlined" onClick={onClear} sx={{ borderRadius: 1, fontWeight: 700, textTransform: "none" }}>
          Clear
        </Button>
        <Button variant="contained" onClick={onSearch} sx={{ borderRadius: 1, fontWeight: 700, textTransform: "none" }}>
          Search
        </Button>
      </Stack>
    </Box>
  );
}

function OrdersTable({
  orders,
  searchText,
  headerText,
  onOrderClick,
  onQuickView,
  showResultLimitNotice,
  onExport,
  stickyTop
}: {
  orders: OrderSearchDocument[];
  searchText: string;
  headerText: string;
  onOrderClick: (order: OrderSearchDocument) => void;
  onQuickView: (orderNumber: string) => void;
  showResultLimitNotice: boolean;
  onExport?: () => void;
  stickyTop: number;
}) {
  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          display: { xs: "block", md: "none" },
          borderRadius: 1,
          overflow: "hidden",
          borderColor: (theme) => alpha(theme.palette.grey[500], 0.2)
        }}
      >
        <Box sx={{ px: 1.5, py: 1.25, backgroundColor: "background.neutral" }}>
          <Typography sx={{ color: "primary.dark", fontWeight: 800 }}>{headerText}</Typography>
          {showResultLimitNotice && (
            <Typography variant="caption" color="text.secondary">
              Top 200 shown. Refine your search for fewer results.
            </Typography>
          )}
        </Box>
        <Divider />
        <Stack divider={<Divider flexItem />}>
          {orders.map((order) => {
            const address = [order.address, order.city, order.state].filter(Boolean).join(" ");
            const closingDate = order.settlementDate ? format(new Date(order.settlementDate), "MM/dd/yyyy") : "Open";

            return (
              <Box
                key={order.rootId || order.orderNumber}
                component="button"
                type="button"
                onClick={() => onOrderClick(order)}
                sx={{
                  width: "100%",
                  border: 0,
                  p: 1.5,
                  display: "block",
                  color: "text.primary",
                  backgroundColor: "background.paper",
                  font: "inherit",
                  textAlign: "left",
                  WebkitTapHighlightColor: "transparent",
                  "&:active": { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.06) }
                }}
              >
                <Stack direction="row" spacing={1} alignItems="stretch">
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ color: "primary.dark", fontSize: 15, fontWeight: 700, overflowWrap: "anywhere" }}>
                      {highlight(order.orderNumber, searchText)}
                    </Typography>
                    {order.projectName && (
                      <Typography sx={{ mt: 0.25, fontSize: 13, fontWeight: 600 }}>
                        {highlight(order.projectName, searchText)}
                      </Typography>
                    )}
                    {address && (
                      <Typography color="text.secondary" sx={{ mt: 0.4, fontSize: 13 }}>
                        {highlight(address, searchText)}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      Closing: {closingDate}
                    </Typography>
                  </Box>
                  {/* Status pinned top-right, info pinned bottom-right. No `spacing`
                      prop here: its margin rules outrank sx, and `gap` leaves the
                      justifyContent split intact. */}
                  <Stack
                    alignItems="flex-end"
                    justifyContent={order.status ? "space-between" : "flex-end"}
                    sx={{ flexShrink: 0, gap: 0.75 }}
                  >
                    {order.status && (
                      <Chip
                        variant="outlined"
                        label={order.status}
                        size="small"
                        sx={{
                          height: 26,
                          borderRadius: 1,
                          fontSize: 12,
                          fontWeight: 700,
                          borderColor: getStatusColor(order.status),
                          color: getStatusColor(order.status)
                        }}
                      />
                    )}
                    <Box
                      component="span"
                      role="button"
                      aria-label={`Quick view ${order.orderNumber}`}
                      tabIndex={0}
                      onClick={(event) => {
                        // The whole row is a button, so stop this from opening the order.
                        event.stopPropagation();
                        onQuickView(order.orderNumber);
                      }}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        cursor: "pointer",
                        color: "primary.dark",
                        backgroundColor: "background.paper",
                        border: "1px solid",
                        borderColor: (theme) => alpha(theme.palette.grey[500], 0.32),
                        boxShadow: "0 2px 6px rgba(22, 28, 36, 0.12)",
                        "&:active": {
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08)
                        }
                      }}
                    >
                      {/* Bare "i" glyph — the button already supplies the circle, so a
                          circled info icon reads as a ring inside a ring. */}
                      <Iconify icon="mdi:information-variant" width={22} />
                    </Box>
                  </Stack>
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          // Lets the card shrink to the space the search bar leaves; without it a
          // flex item refuses to go below its content height.
          minHeight: 0,
          borderRadius: 1,
          borderColor: (theme) => alpha(theme.palette.grey[500], 0.2),
          boxShadow: "0 2px 8px rgba(22, 28, 36, 0.04)"
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.neutral",
            position: "sticky",
            top: stickyTop,
            zIndex: 3,
            borderRadius: "4px 4px 0 0"
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" sx={{ flexGrow: 1, color: "primary.dark", fontWeight: 700 }}>
              {headerText}
              {showResultLimitNotice && (
                <Typography component="span" sx={{ ml: 1, color: "text.secondary", fontSize: "0.875rem" }}>
                  (Top 200 only shown; refine your search or use Advanced Search to narrow it down.)
                </Typography>
              )}
            </Typography>
            {onExport && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<Iconify icon="eva:download-fill" />}
                onClick={onExport}
                sx={{ borderRadius: 1, fontWeight: 700, textTransform: "none" }}
              >
                Export
              </Button>
            )}
          </Stack>
        </Box>
        <TableContainer
          sx={{
            // The card's height now comes from the page flex, so the table just takes
            // whatever is left instead of a fixed viewport guess.
            minHeight: 0,
            overflow: "auto",
            borderRadius: "0 0 4px 4px"
          }}
        >
          <Table
            stickyHeader
            size="small"
            // Percentage columns + fixed layout keep the row inside the card at any
            // width; px minimums used to force horizontal overflow on smaller screens.
            sx={{ tableLayout: "fixed", width: "100%" }}
          >
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    backgroundColor: "background.paper",
                    color: "text.secondary",
                    fontWeight: 700
                  }
                }}
              >
                <TableCell sx={{ width: "15%" }}>Order Number</TableCell>
                <TableCell sx={{ width: "12%" }}>Project Name</TableCell>
                <TableCell sx={{ width: "9%" }}>Closing Date</TableCell>
                <TableCell sx={{ width: "19%" }}>Address</TableCell>
                <TableCell sx={{ width: "5%" }}>State</TableCell>
                <TableCell sx={{ width: "12%" }}>Block/Lot/Parcel</TableCell>
                <TableCell sx={{ width: "18%" }}>Marketing Source/Buyer/Seller</TableCell>
                <TableCell sx={{ width: "10%" }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow
                  key={order.rootId || order.orderNumber}
                  hover
                  onClick={() => onOrderClick(order)}
                  sx={{
                    cursor: "pointer",
                    height: 70,
                    "& td": {
                      verticalAlign: "top",
                      py: 1,
                      overflowWrap: "anywhere",
                      borderColor: (theme) => alpha(theme.palette.grey[500], 0.16)
                    },
                    "&:hover": {
                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  <TableCell sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    {highlight(order.orderNumber, searchText)}
                  </TableCell>
                  <TableCell>{highlight(order.projectName, searchText)}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {order.settlementDate ? format(new Date(order.settlementDate), "MM/dd/yyyy") : "Open"}
                  </TableCell>
                  <TableCell>
                    {highlight([order.address, order.city].filter(Boolean).join(" "), searchText)}
                    {!!order.additionalPropertyAddresses?.length && (
                      <>
                        <br />
                        {highlight(order.additionalPropertyAddresses.join(", "), searchText)}
                      </>
                    )}
                  </TableCell>
                  <TableCell>{highlight(order.state, searchText)}</TableCell>
                  <TableCell>{highlight(getBlockLotParcel(order), searchText)}</TableCell>
                  <TableCell>
                    {order.marketingSourceType && `${order.marketingSourceType}: `}
                    {highlight(order.marketingSource, searchText)}
                    {order.marketingSourceType && <br />}
                    {order.buyer && (
                      <>
                        Buyer: {highlight(order.buyer, searchText)}
                        <br />
                      </>
                    )}
                    {order.seller && (
                      <>
                        Seller: {highlight(order.seller, searchText)}
                        <br />
                      </>
                    )}
                    {order.principal && <>Principal: {highlight(order.principal, searchText)}</>}
                  </TableCell>
                  <TableCell>
                    {order.status && (
                      <Chip
                        variant="outlined"
                        label={order.status}
                        size="small"
                        sx={{
                          width: "100%",
                          maxWidth: 100,
                          borderRadius: 1,
                          fontWeight: 700,
                          borderColor: getStatusColor(order.status),
                          color: getStatusColor(order.status)
                        }}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}

function getBlockLotParcel(order: OrderSearchDocument) {
  const parts: string[] = [];
  if (order.blocks?.length) {
    parts.push(`${order.blocks.length === 1 ? "Block" : "Blocks"}: ${order.blocks.join(" & ")}`);
  }
  if (order.lots?.length) {
    parts.push(`${order.lots.length === 1 ? "Lot" : "Lots"}: ${order.lots.join(" & ")}`);
  }
  if (order.parcels?.length) {
    parts.push(`${order.parcels.length === 1 ? "Parcel" : "Parcels"}: ${order.parcels.join(" & ")}`);
  }

  return parts.length ? parts.join(" ") : (order.escrowBriefLegal ?? "");
}

function escapeCsvValue(value?: string | number | Date | null) {
  const stringValue = value == null ? "" : String(value);
  const excelSafeValue = /^[=+\-@]/.test(stringValue) ? `'${stringValue}` : stringValue;
  return `"${excelSafeValue.replace(/"/g, '""')}"`;
}

function formatExportDate(date?: string) {
  if (!date) {
    return "Open";
  }

  const parsedDate = new Date(date);
  return Number.isNaN(parsedDate.getTime()) ? "" : parsedDate.toLocaleDateString();
}

function highlight(value?: string, searchText?: string) {
  if (!value || !searchText) {
    return value ?? "";
  }

  const words = searchText.split(/\s+/).filter(Boolean);
  if (!words.length) {
    return value;
  }

  const pattern = new RegExp(`(${words.map(escapeRegex).join("|")})`, "gi");
  return value.split(pattern).map((part, index) =>
    words.some((word) => part.toLowerCase() === word.toLowerCase()) ? (
      <Box key={`${part}-${index}`} component="mark" sx={{ px: 0.25, backgroundColor: "#fff4a3", borderRadius: 0.25 }}>
        {part}
      </Box>
    ) : (
      part
    )
  );
}

function escapeRegex(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function getStatusColor(status: string) {
  switch (status) {
    case "In process":
    case "Transferred":
      return "#3b49d4";
    case "Tentative":
      return "#252f85";
    case "Hold":
      return "#7e60ab";
    case "Pending":
      return "#552899";
    case "Firm":
      return "#47a842";
    case "Completed":
      return "#2a8a25";
    case "Closed":
      return "#1a5417";
    case "Adjourned":
      return "#e0a800";
    case "Canceled":
      return "#ff2929";
    case "Duplicate":
      return "#ac0000";
    case "Dormant":
      return "#78909c";
    default:
      return "#000000";
  }
}

function getOrderHistory(): OrderSearchDocument[] {
  const json = window.localStorage.getItem(storageKey);
  if (!json) {
    return [];
  }

  try {
    return JSON.parse(json);
  } catch {
    return [];
  }
}

function addOrderToHistory(order: OrderSearchDocument) {
  const existingOrders = getOrderHistory()
    .filter((item) => item.orderNumber !== order.orderNumber)
    .slice(0, maxHistorySize - 1);
  window.localStorage.setItem(storageKey, JSON.stringify([order, ...existingOrders]));
}
